import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Spin, Typography } from 'antd';
import dayjs from 'dayjs';

import { getPendingApprovals } from '@totem/components/ticketing/approval_utilities';
import OptimizeDeferralButton from '@totem/components/ticketing/details/optimize/optimizeDeferralButton';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import {
  calculateAnnualSavings,
  calculatePaybackMonths,
  calculateTotalSavings,
  formatPaybackPeriod,
} from '@totem/components/ticketing/ticket/specialCases/utilities';
import { SelectedApprovals } from '@totem/components/ticketing/ticketing';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Status label helpers
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  Draft: { label: 'DRAFT · IN PREPARATION', color: '#94A3B8' },
  'Pending Review': { label: 'AWAITING YOUR APPROVAL', color: '#FB923C' },
  Approved: { label: 'APPROVED · SCHEDULED', color: '#60A5FA' },
  'In Progress': { label: 'IN PROGRESS', color: '#60A5FA' },
  Verifying: { label: 'VERIFYING SAVINGS', color: '#34D399' },
  'Savings Booked': { label: 'SAVINGS REALIZED', color: '#34D399' },
  Rejected: { label: 'REJECTED', color: '#F87171' },
  Deferred: { label: 'DEFERRED', color: '#94A3B8' },
  Cancelled: { label: 'CANCELLED', color: '#F87171' },
};

const getStatusLabel = (status: string) =>
  STATUS_LABELS[status] ?? { label: status.toUpperCase(), color: '#94A3B8' };

// Status values where the "Approve opportunity" action is available
const APPROVABLE_STATUSES = new Set(['Pending Review', 'Draft']);
const REJECTABLE_STATUSES = new Set([
  'Draft',
  'Pending Review',
  'Approved',
  'In Progress',
]);

// ---------------------------------------------------------------------------
// Sidebar styles (dark panel)
// ---------------------------------------------------------------------------

const SIDEBAR_BG = colors.branding.primaryDark;
const CARD_BG = '#1E293B';
const BORDER_COLOR = '#334155';
const TEXT_PRIMARY = '#FFFFFF';
const TEXT_SECONDARY = '#94A3B8';
const TEXT_LABEL = '#64748B';

const metaRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  paddingBottom: 6,
  borderBottom: `1px solid ${BORDER_COLOR}`,
  marginBottom: 6,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OpportunitySidebar = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { ticket, loading, onAction, onOptimizeStatusChange } =
    useContext(TicketContext);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [approving, setApproving] = useState(false);
  const [approvalSending, setApprovalSending] = useState(false);
  const [approvalReason, setApprovalReason] = useState('');
  const [pendingApprovals, setPendingApprovals] = useState<SelectedApprovals[]>(
    [],
  );

  const [searchParams] = useSearchParams();

  const fromTab = searchParams.get('f');

  useEffect(() => {
    if (isNotNull(ticket) && isNotNull(userProfile)) {
      setPendingApprovals(getPendingApprovals(userProfile.email, ticket));
    }
  }, [ticket, userProfile]);

  const sendApprovalUpdate = useCallback(
    (selected: SelectedApprovals) => {
      if (approvalSending || !ticket) return;
      setApprovalSending(true);

      fetch(
        `${TICKET_ENDPOINT}/${ticket.ticket.id}/approval/${selected.approvalRequest.id}`,
        {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(selected.approval),
        },
      ).then((res) => {
        setApprovalSending(false);
        const success = CheckResponseShowError(res);
        if (success) {
          setApprovalReason('');

          onAction('refresh', '');
        }
      });
    },
    [approvalSending, ticket, getApiToken, onAction],
  );

  const handleApprovalApprove = (selected: SelectedApprovals) => {
    sendApprovalUpdate({
      approvalRequest: selected.approvalRequest,
      approval: {
        ...selected.approval,
        status: 'APPROVED',
        reason: approvalReason,
        outcomeAt: dayjs().toISOString(),
      },
    });
  };

  const handleApprovalReject = (selected: SelectedApprovals) => {
    sendApprovalUpdate({
      approvalRequest: selected.approvalRequest,
      approval: {
        ...selected.approval,
        status: 'REJECTED',
        reason: approvalReason,
        outcomeAt: dayjs().toISOString(),
      },
    });
  };

  if (loading || !ticket) {
    return (
      <div
        style={{
          width: 360,
          position: 'sticky',
          top: 0,
          height: 'calc(100vh - 64px)',
          overflowY: 'auto',
          background: SIDEBAR_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin />
      </div>
    );
  }

  const { ticket: tkData, building } = ticket;
  const opt = tkData.extras?.optimize ?? null;
  const opportunityStatus = opt?.opportunityStatus ?? '';
  const statusConfig = getStatusLabel(opportunityStatus);

  const annualSavings = isNotNull(opt) ? calculateAnnualSavings(opt) : 0;
  const fiveYearTotal = isNotNull(opt) ? calculateTotalSavings(opt) : 0;
  const paybackMonths = isNotNull(opt) ? calculatePaybackMonths(opt) : null;

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);

  const handleApprove = () => {
    if (!onOptimizeStatusChange) return;
    setApproving(true);

    onOptimizeStatusChange({
      fromStatus: opportunityStatus,
      toStatus: 'Approved',
      changedBy: '',
      changedAt: new Date().toISOString(),
      notes: '',
    });

    setApproving(false);
  };

  const handleReject = () => {
    if (!onOptimizeStatusChange) return;
    onOptimizeStatusChange({
      fromStatus: opportunityStatus,
      toStatus: 'Rejected',
      changedBy: '',
      changedAt: new Date().toISOString(),
      notes: rejectNotes,
    });

    setRejectOpen(false);

    setRejectNotes('');
  };

  return (
    <div
      style={{
        width: 360,
        minWidth: 360,
        position: 'sticky',
        top: 0,
        height: 'calc(100vh - 64px)',
        overflowY: 'auto',
        background: SIDEBAR_BG,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 24px',
        boxSizing: 'border-box',
      }}
    >
      {/* Breadcrumb */}
      <div style={{ marginBottom: 20 }}>
        <Link
          to={
            isNotNull(fromTab)
              ? `/dashboard/optimize2?t=${fromTab}`
              : '/dashboard/optimize2'
          }
          style={{ fontSize: 12, color: TEXT_SECONDARY }}
        >
          ← Optimize
        </Link>
      </div>

      {/* Ticket number + category badges */}
      <div
        style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}
      >
        <span
          style={{
            background: CARD_BG,
            color: TEXT_SECONDARY,
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 4,
            border: `1px solid ${BORDER_COLOR}`,
            fontFamily: 'monospace',
          }}
        >
          <Link to={`/dashboard/optimize/ticket/${tkData.id}`}>
            {tkData.number}
          </Link>
        </span>
        {isNotNull(opt) && opt.category && (
          <span
            style={{
              background: CARD_BG,
              color: TEXT_SECONDARY,
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 4,
              border: `1px solid ${BORDER_COLOR}`,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {opt.category}
          </span>
        )}
      </div>

      {/* Status label */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: statusConfig.color,
          marginBottom: 8,
          textTransform: 'uppercase',
        }}
      >
        {statusConfig.label}
      </div>

      {/* Title */}
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: TEXT_PRIMARY,
          lineHeight: 1.25,
          marginBottom: 20,
        }}
      >
        {tkData.summary}
      </div>

      {/* 5-Year Savings card */}
      <div
        style={{
          background: CARD_BG,
          borderRadius: 8,
          padding: '16px 18px',
          marginBottom: 16,
          border: `1px solid ${BORDER_COLOR}`,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: TEXT_LABEL,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Avoided Cost Over{' '}
          {isNotNull(opt)
            ? `${opt.savingsTerm} ${opt.savingsTermPeriod === 'Year' || opt.savingsTermPeriod === 'Years' ? 'Years' : 'Months'}`
            : '5 Years'}
        </div>
        <div
          style={{
            fontSize: 36,
            fontWeight: 700,
            color: TEXT_PRIMARY,
            lineHeight: 1.1,
            marginBottom: 6,
          }}
        >
          {formatCurrency(fiveYearTotal)}
        </div>
        <div style={{ fontSize: 12, color: TEXT_SECONDARY }}>
          {formatCurrency(annualSavings)}/year
          {isNotNull(opt) && opt.requiredInvestment === 0
            ? ' · No capital required'
            : isNotNull(opt) && opt.requiredInvestment > 0
              ? ` · ${formatCurrency(opt.requiredInvestment)} investment`
              : ''}
        </div>
      </div>

      {/* Stats grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px 16px',
          marginBottom: 24,
        }}
      >
        {[
          {
            label: 'INVEST',
            value:
              isNotNull(opt) && opt.requiredInvestment === 0
                ? '$0'
                : isNotNull(opt)
                  ? formatCurrency(opt.requiredInvestment)
                  : '—',
          },
          {
            label: 'EFFORT',
            value: isNotNull(opt) ? opt.levelOfEffort || '—' : '—',
          },
          {
            label: 'TERM',
            value: isNotNull(opt)
              ? `${opt.savingsTerm}${opt.savingsTermPeriod === 'Year' || opt.savingsTermPeriod === 'Years' ? 'y' : 'mo'}`
              : '—',
          },
          {
            label: 'PAYBACK',
            value: formatPaybackPeriod(paybackMonths),
          },
        ].map(({ label, value }) => (
          <div key={label}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: TEXT_LABEL,
                textTransform: 'uppercase',
                marginBottom: 2,
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: TEXT_PRIMARY,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Pending approval requests */}
      {pendingApprovals.map((selected) => (
        <div
          key={selected.approval.id}
          style={{
            background: CARD_BG,
            border: `1px solid #D97706`,
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              color: '#F59E0B',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Pending Approval
          </div>
          {selected.approvalRequest.summary && (
            <div
              style={{
                fontSize: 12,
                color: TEXT_SECONDARY,
                marginBottom: 10,
                lineHeight: 1.4,
              }}
            >
              {selected.approvalRequest.summary}
            </div>
          )}
          <Input.TextArea
            rows={2}
            value={approvalReason}
            onChange={(evt) => setApprovalReason(evt.target.value)}
            placeholder="Reason (optional)"
            style={{
              marginBottom: 10,
              background: '#0F172A',
              borderColor: BORDER_COLOR,
              color: TEXT_PRIMARY,
              fontSize: 12,
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              icon={<CheckCircleOutlined />}
              loading={approvalSending}
              onClick={() => handleApprovalApprove(selected)}
              type={'primary'}
              style={{
                flex: 1,
                fontWeight: 600,
              }}
            >
              Approve
            </Button>
            <Button
              icon={<StopOutlined />}
              loading={approvalSending}
              onClick={() => handleApprovalReject(selected)}
              style={{
                flex: 1,
                background: 'transparent',
                borderColor: BORDER_COLOR,
                color: TEXT_SECONDARY,
                fontWeight: 600,
              }}
            >
              Reject
            </Button>
          </div>
        </div>
      ))}

      {/* Action buttons */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          marginBottom: 16,
        }}
      >
        {APPROVABLE_STATUSES.has(opportunityStatus) && (
          <Button
            type="primary"
            loading={approving}
            onClick={handleApprove}
            style={{
              background: '#E05A2B',
              borderColor: '#E05A2B',
              height: 44,
              fontSize: 15,
              fontWeight: 600,
              borderRadius: 8,
            }}
            block
          >
            Approve opportunity
          </Button>
        )}

        <OptimizeDeferralButton />

        {REJECTABLE_STATUSES.has(opportunityStatus) && (
          <button
            onClick={() => setRejectOpen(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: TEXT_SECONDARY,
              fontSize: 13,
              cursor: 'pointer',
              padding: '4px 0',
              textAlign: 'center',
            }}
          >
            Reject with reason
          </button>
        )}
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Metadata */}
      <div
        style={{
          borderTop: `1px solid ${BORDER_COLOR}`,
          paddingTop: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {building?.name && (
          <div style={metaRow}>
            <Text style={{ fontSize: 12, color: TEXT_LABEL }}>Building</Text>
            <Text style={{ fontSize: 12, color: TEXT_SECONDARY }}>
              {building.name}
            </Text>
          </div>
        )}
        {isNotNull(opt) && opt.category && (
          <div style={metaRow}>
            <Text style={{ fontSize: 12, color: TEXT_LABEL }}>Category</Text>
            <Text style={{ fontSize: 12, color: TEXT_SECONDARY }}>
              {opt.category}
            </Text>
          </div>
        )}
        {tkData.createdAt && (
          <div style={metaRow}>
            <Text style={{ fontSize: 12, color: TEXT_LABEL }}>Detected</Text>
            <Text style={{ fontSize: 12, color: TEXT_SECONDARY }}>
              {new Date(tkData.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </Text>
          </div>
        )}
        {isNotNull(opt) && opt.levelOfEffort && (
          <div style={{ ...metaRow, borderBottom: 'none', marginBottom: 0 }}>
            <Text style={{ fontSize: 12, color: TEXT_LABEL }}>Effort</Text>
            <Text style={{ fontSize: 12, color: TEXT_SECONDARY }}>
              {opt.levelOfEffort}
            </Text>
          </div>
        )}
      </div>

      {/* Reject modal */}
      <Modal
        open={rejectOpen}
        title="Reject with reason"
        onCancel={() => setRejectOpen(false)}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#374151' }}>
            Please provide a reason for rejection (optional):
          </div>
          <textarea
            rows={4}
            value={rejectNotes}
            onChange={(evt) => setRejectNotes(evt.target.value)}
            placeholder="e.g. Not aligned with current capital plan"
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              fontSize: 13,
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default OpportunitySidebar;
